import { Track } from "@/src/lib/tracking";
/**
 * Operation Debouncer - 操作防抖管理
 *
 * 负责管理操作历史记录、智能防抖和API调用合并
 */

import type { FavoritesCenterState } from "../types";
import type { PositionsAccountService } from "../PositionsAccountService";
import { OptimisticUpdateManager } from "./OptimisticUpdateManager";
import { UserStateManager } from "./UserStateManager";
import { updateVersionedData } from "./VersionedDataManager";
import type { PositionAssetTypeEnum } from "@features/api/memberApi";

export class OperationDebouncer {
  private debounceTimers: Map<string, NodeJS.Timeout> = new Map();
  // Operation history storage: contentId -> Map<folderId, action[]>
  private operationHistory: Map<string, Map<string, ("ADD" | "REMOVE")[]>> =
    new Map();

  constructor(
    private positionsAccountService: PositionsAccountService | null,
    private onRollback: (
      contentId: string,
      folderId: string,
      action: "ADD" | "REMOVE",
    ) => void,
    private onError: (error: Error) => void,
    // New dependencies for state re-application after API success
    private userStateManager?: UserStateManager,
    private optimisticUpdateManager?: OptimisticUpdateManager,
    private state?: FavoritesCenterState,
    private getCurrentUserId?: () => string | null,
    private updateUserFoldersAssetCount?: (
      folderId: string,
      action: "ADD" | "REMOVE",
    ) => void,
    private onMutationApiSuccess?: (finalAction: "ADD" | "REMOVE") => void,
  ) {}

  /**
   * Record operation history
   */
  recordOperationHistory(
    contentId: string,
    folderId: string,
    action: "ADD" | "REMOVE",
  ): void {
    // Get or create operation history for contentId
    if (!this.operationHistory.has(contentId)) {
      this.operationHistory.set(contentId, new Map());
    }

    const contentHistory = this.operationHistory.get(contentId)!;

    // Get or create operation array for folderId
    if (!contentHistory.has(folderId)) {
      contentHistory.set(folderId, []);
    }

    const folderActions = contentHistory.get(folderId)!;
    folderActions.push(action);
  }

  /**
   * Smart debounced API call
   */
  smartDebouncedApiCall(
    contentId: string,
    folderId: string,
    action: "ADD" | "REMOVE",
    assetType: "TOKEN" | "STOCK" = "TOKEN",
  ): void {
    const key = `${contentId}_${folderId}`;

    // Clear previous timer
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key)!);
    }

    // Set new timer (2 second delay)
    const timer = setTimeout(async () => {
      try {
        // Check operation history to determine if API should be called
        const shouldCallApi = this.shouldCallApi(contentId, folderId);

        if (!shouldCallApi) {
          this.clearOperationHistory(contentId, folderId);
          return;
        }

        // Get final action type
        const finalAction = this.getFinalAction(contentId, folderId);

        if (!this.positionsAccountService) {
          Track.log(
            "error",
            "OperationDebouncer",
            "PositionsAccountService not initialized",
          );
          throw new Error("PositionsAccountService not initialized");
        }

        let result;
        if (finalAction === "ADD") {
          result = await this.positionsAccountService.addUserPosition(
            folderId,
            [
              {
                asset_id: contentId,
                asset_type: assetType as PositionAssetTypeEnum,
              },
            ],
          );
        } else {
          result = await this.positionsAccountService.removeUserPosition(
            folderId,
            {
              asset_id: contentId,
              asset_type: assetType as PositionAssetTypeEnum,
            },
          );
        }

        if (result.success) {
          this.debounceTimers.delete(key);

          // Re-apply state update after API success to ensure consistency
          // This fixes the race condition where fetchFolderContents might have
          // overwritten optimistic updates before API completed
          this.reapplyStateUpdate(contentId, folderId, finalAction);

          this.clearOperationHistory(contentId, folderId);
          Track.log(
            "info",
            "OperationDebouncer",
            `API call completed: ${contentId} ${finalAction} ${folderId}`,
          );
          this.onMutationApiSuccess?.(finalAction);
        } else {
          throw new Error(result.message || "API call failed");
        }
      } catch (error) {
        Track.error(error, {
          tag: "OperationDebouncer",
          msg: "API call failed:",
        });

        // Rollback state on API failure
        this.onRollback(contentId, folderId, action);

        this.onError(error as Error);
      }
    }, 1000);

    this.debounceTimers.set(key, timer);
  }

  /**
   * Check if API should be called
   */
  private shouldCallApi(contentId: string, folderId: string): boolean {
    const contentHistory = this.operationHistory.get(contentId);
    if (!contentHistory) {
      return false;
    }

    const folderActions = contentHistory.get(folderId);
    if (!folderActions || folderActions.length === 0) {
      return false;
    }

    // Check if first and last actions are the same
    const firstAction = folderActions[0];
    const lastAction = folderActions[folderActions.length - 1];

    const shouldCall = firstAction === lastAction;

    return shouldCall;
  }

  /**
   * Get final action type
   */
  private getFinalAction(
    contentId: string,
    folderId: string,
  ): "ADD" | "REMOVE" {
    const contentHistory = this.operationHistory.get(contentId);
    if (!contentHistory) {
      return "ADD"; // Default value
    }

    const folderActions = contentHistory.get(folderId);
    if (!folderActions || folderActions.length === 0) {
      return "ADD"; // Default value
    }

    return folderActions[folderActions.length - 1];
  }

  /**
   * Clear operation history
   */
  clearOperationHistory(contentId: string, folderId: string): void {
    const contentHistory = this.operationHistory.get(contentId);
    if (contentHistory) {
      contentHistory.delete(folderId);

      // If this contentId has no more operation history, delete the entire record
      if (contentHistory.size === 0) {
        this.operationHistory.delete(contentId);
      }
    }
  }

  /**
   * Clear all timers (for cleanup)
   */
  clearAllTimers(): void {
    this.debounceTimers.forEach((timer) => clearTimeout(timer));
    this.debounceTimers.clear();
  }

  /**
   * Re-apply state update after API success
   * This ensures state consistency even if fetchFolderContents overwrote optimistic updates
   */
  private reapplyStateUpdate(
    contentId: string,
    folderId: string,
    finalAction: "ADD" | "REMOVE",
  ): void {
    // Skip if dependencies are not available
    if (
      !this.userStateManager ||
      !this.optimisticUpdateManager ||
      !this.state ||
      !this.getCurrentUserId
    ) {
      Track.log(
        "warn",
        "OperationDebouncer",
        "Cannot reapply state: dependencies not available",
      );
      return;
    }

    try {
      const currentUserId = this.getCurrentUserId();
      if (!currentUserId) {
        Track.log(
          "warn",
          "OperationDebouncer",
          "Cannot reapply state: user not logged in",
        );
        return;
      }

      // Get current state
      const currentState = this.state.favoriteStates.data[contentId] || [];
      let newState: string[];

      // Calculate final state based on final action
      if (finalAction === "ADD") {
        // Ensure folderId is in the array
        newState = currentState.includes(folderId)
          ? currentState
          : [...currentState, folderId];
      } else {
        // Remove folderId from array
        newState = currentState.filter((id) => id !== folderId);
      }

      // Update favoriteStates using versioned data mechanism
      const newData = { ...this.state.favoriteStates.data };
      newData[contentId] = newState;
      updateVersionedData(this.state.favoriteStates, newData, true);

      // Update source data cache
      const userData = this.userStateManager.getOrCreateUserData(currentUserId);
      userData.favoriteStates.set(contentId, newState);
      userData.lastAccess = Date.now();

      // Update allFavoriteData summary data
      this.optimisticUpdateManager.updateAllFavoriteData([
        { key: contentId, value: newState },
      ]);

      // Update asset_count in userFolders (if callback available)
      if (this.updateUserFoldersAssetCount) {
        this.updateUserFoldersAssetCount(folderId, finalAction);
      }
    } catch (error) {
      Track.error(error, {
        tag: "OperationDebouncer",
        msg: "Failed to reapply state after API success:",
      });
      // Don't throw - API already succeeded, this is just state sync
    }
  }

  /**
   * Update positions account service (called after setApiClient)
   */
  updatePositionsAccountService(service: PositionsAccountService | null): void {
    this.positionsAccountService = service;
  }
}
